import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Form, Button, Row, Col, Container, Alert } from 'react-bootstrap';
import { push } from '../../modules/history';
import Logo from '../../../assets/media/images/Logo.png';
import config from '../../config';

const validationSchema = Yup.object().shape({
  businessName: Yup.string().required('Business name is required'),
  email: Yup.string()
    .required('Required')
    .email('Invalid email'),
  password: Yup.string()
    .required('Required')
    .min(8, 'At least 8 characters'),
  confirmPassword: Yup.string()
    .required('Required')
    .oneOf([Yup.ref('password')], 'Passwords do not match'),
  category: Yup.string().required('Category is required'),
  address: Yup.string().required('Address is required'),
  phone: Yup.string(),
});

const CATEGORIES = [
  'Restaurant',
  'Retail',
  'Grocery',
  'Gas Station',
  'Pharmacy',
  'Coffee Shop',
  'Entertainment',
  'Health & Beauty',
  'Travel',
  'Other',
];

export default class BusinessSignupView extends Component {
  static propTypes = {
    error: PropTypes.string,
    loading: PropTypes.bool,
    onSubmit: PropTypes.func.isRequired,
    success: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      suggestions: [],
      showSuggestions: false,
      loadingSuggestions: false,
    };
  }

  debounceTimer = null;

  fetchSuggestions = async value => {
    if (value.trim().length < 3) {
      this.setState({ suggestions: [], showSuggestions: false });
      return;
    }
    this.setState({ loadingSuggestions: true });
    try {
      const res = await fetch(
        `${config.BASE_URL}/businesses/address-autocomplete?q=${encodeURIComponent(value.trim())}`,
      );
      const json = await res.json();
      const suggestions = json?.data?.result ?? json?.result ?? [];
      this.setState({
        suggestions,
        showSuggestions: suggestions.length > 0,
        loadingSuggestions: false,
      });
    } catch {
      this.setState({ suggestions: [], showSuggestions: false, loadingSuggestions: false });
    }
  };

  onAddressChange = (e, handleChange, setFieldValue) => {
    handleChange(e);
    setFieldValue('latitude', null);
    setFieldValue('longitude', null);
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.fetchSuggestions(e.target.value), 350);
  };

  selectSuggestion = (suggestion, setFieldValue) => {
    setFieldValue('address', suggestion.address);
    setFieldValue('latitude', suggestion.latitude);
    setFieldValue('longitude', suggestion.longitude);
    this.setState({ suggestions: [], showSuggestions: false });
  };

  render() {
    const { onSubmit, loading, success, error } = this.props;

    if (success) {
      return (
        <Container className="pt-5">
          <Row className="justify-content-center">
            <Col xs={12} sm={8} md={6}>
              <div className="text-center pb-4">
                <img src={Logo} alt="YieldBucks" width="200px" />
              </div>
              <Alert variant="success">
                <Alert.Heading>Application Submitted!</Alert.Heading>
                <p>
                  Your business account application has been submitted. Our team will review and
                  approve your account within 1–2 business days. You will receive an email once your
                  account is approved.
                </p>
                <hr />
                <Button variant="outline-success" onClick={() => push('/login')}>
                  Go to Login
                </Button>
              </Alert>
            </Col>
          </Row>
        </Container>
      );
    }

    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col xs={12} sm={10} md={8} lg={6}>
            <div className="text-center mb-4">
              <img src={Logo} alt="YieldBucks" width="200px" className="mb-3" />
              <h2 style={{ fontWeight: 700, textAlign: 'center' }}>Business Account Application</h2>
              <p className="text-muted mb-0">
                Register your business to offer rewards to YieldBucks users.
              </p>
            </div>

            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}

            <Formik
              enableReinitialize
              initialValues={{
                businessName: '',
                email: '',
                password: '',
                confirmPassword: '',
                category: '',
                address: '',
                latitude: null,
                longitude: null,
                phone: '',
              }}
              validationSchema={validationSchema}
              onSubmit={values => {
                const rest = { ...values };
                delete rest.confirmPassword;
                onSubmit(rest);
              }}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                setFieldValue,
              }) => (
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3" controlId="businessName">
                    <Form.Label>
                      <strong>Business Name *</strong>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="businessName"
                      placeholder="Your business name"
                      value={values.businessName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.businessName && !!errors.businessName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.businessName}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="email">
                    <Form.Label>
                      <strong>Business Email *</strong>
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      placeholder="business@example.com"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.email && !!errors.email}
                    />
                    <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                  </Form.Group>

                  <Row>
                    <Col sm={6}>
                      <Form.Group className="mb-3" controlId="password">
                        <Form.Label>
                          <strong>Password *</strong>
                        </Form.Label>
                        <Form.Control
                          type="password"
                          name="password"
                          placeholder="Min 8 characters"
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.password && !!errors.password}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.password}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col sm={6}>
                      <Form.Group className="mb-3" controlId="confirmPassword">
                        <Form.Label>
                          <strong>Confirm Password *</strong>
                        </Form.Label>
                        <Form.Control
                          type="password"
                          name="confirmPassword"
                          placeholder="Repeat password"
                          value={values.confirmPassword}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.confirmPassword && !!errors.confirmPassword}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.confirmPassword}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3" controlId="category">
                    <Form.Label>
                      <strong>Category *</strong>
                    </Form.Label>
                    <Form.Control
                      as="select"
                      name="category"
                      value={values.category}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.category && !!errors.category}
                    >
                      <option value="">Select a category...</option>
                      {CATEGORIES.map(c => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">{errors.category}</Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="address">
                    <Form.Label>
                      <strong>Business Address *</strong>
                    </Form.Label>
                    <div style={{ position: 'relative' }}>
                      <Form.Control
                        type="text"
                        name="address"
                        placeholder="Start typing your address…"
                        value={values.address}
                        onChange={e => this.onAddressChange(e, handleChange, setFieldValue)}
                        onBlur={() => {
                          handleBlur({ target: { name: 'address' } });
                          setTimeout(() => this.setState({ showSuggestions: false }), 200);
                        }}
                        isInvalid={touched.address && !!errors.address}
                        autoComplete="off"
                      />
                      {this.state.showSuggestions && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            zIndex: 1050,
                            background: '#fff',
                            border: '1px solid #dee2e6',
                            borderRadius: '0 0 6px 6px',
                            boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
                            maxHeight: 240,
                            overflowY: 'auto',
                          }}
                        >
                          {this.state.suggestions.map((s, i) => {
                            const [street, ...rest] = s.label.split(',');
                            return (
                              <div
                                key={i}
                                onMouseDown={() => this.selectSuggestion(s, setFieldValue)}
                                style={{
                                  padding: '10px 14px',
                                  cursor: 'pointer',
                                  borderBottom:
                                    i < this.state.suggestions.length - 1
                                      ? '1px solid #f0f0f0'
                                      : 'none',
                                }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#f8f9fa')}
                                onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                              >
                                <div style={{ fontWeight: 600, fontSize: 14 }}>{street}</div>
                                {rest.length > 0 && (
                                  <div style={{ fontSize: 12, color: '#6c757d', marginTop: 2 }}>
                                    {rest.join(',').trim()}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {values.latitude && (
                      <Form.Text style={{ color: '#28a745' }}>Location confirmed</Form.Text>
                    )}
                    {!values.latitude &&
                      values.address.length > 0 &&
                      !this.state.showSuggestions && (
                        <Form.Text className="text-muted">
                          Select an address from the suggestions to pin your location.
                        </Form.Text>
                      )}
                    <Form.Control.Feedback type="invalid">{errors.address}</Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-4" controlId="phone">
                    <Form.Label>
                      <strong>Phone (optional)</strong>
                    </Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      placeholder="+1 (555) 000-0000"
                      value={values.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Form.Group>

                  <Button
                    variant="primary"
                    size="lg"
                    className="w-100"
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? 'Submitting…' : 'Submit Application'}
                  </Button>
                  <div className="text-center mt-3">
                    <Button variant="link" onClick={() => push('/login')}>
                      Already have an account? Log in
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </Col>
        </Row>
      </Container>
    );
  }
}
